// The DEPTH and the CURRENT sections of the legend card (R2616;
// v2-the-deep.md §18.1; mockup round 088 cards 04 and 07).
//
// **DEPTH** holds four swatches, one for each depth band, in the colour
// the depth tints (card 09) paint the band in the depth look now over the
// water top: the light, the dark and the hue of the depth light (R3229,
// `depthBandCss`), each with the sounding of the shallow end of the band
// (card 02) and the band word. The owner struck the thermocline line on 2026-10-24
// (R2831): the edge seam (card 72) on the map and the sonar lab keep the
// thermocline. The rating of a fleet as a band ("rated to
// the slope", R2565) and the band it is in stand in its card, the depth
// fact and the route profile, because the card follows the hull and the
// legend follows the chart.
//
// **CURRENT** holds a streak of card 07 with the strongest current of
// the map at the tide now, and the tide clock: the factor and the step of
// the table, a bar of the steps with the step now marked, the time of day
// of the next step and the time of day the flow turns round. The clock
// never takes the word "tidal" (v2-the-deep.md §13): the tidal diamond is
// the clock of a strategic object.
//
// The DEPTH heat map (R2870) shows the four bands in the inks of the depth
// heat look (`depthHeatBodyHtml`), and the CURRENTS heat map shows the
// CURRENT body; `deepShown` says which section stands for a heat mode.
//
// The two bodies are pure markup, so the legend card of a desktop
// (`faction-key.ts`) and the VIEW panel of a telephone write the same
// words, and a test reads them with no layout engine. A section stands
// while the hello carries its field and the reader shows its group of
// chart pieces; the callers decide that.

import { WATER_DEEP_TOP } from '../../gen/presentation';
import { depthBandCss, depthHeatCfg, type DepthLook } from '../../depthlook';
import { HeatmapKind } from '../../gen/proto/aquaphobia';
import { channels } from '../../render/color';
import { chart as S } from '../../gen/strings';
import { BANDS, type TideClock } from '../../deep';
import { bandTopMetres } from '../../fast/chart/pieces/soundings';
import { hhmm } from '../../locale/clock';
import { esc } from '../esc';

/** A band word in the reading: SHELF in the modern one, shelf in the
 *  historical one. '' for no band. */
export function bandWord(band: number, hist: boolean): string {
  return (hist ? S.band_words_historical : S.band_words)[band - 1] ?? '';
}

/** A swatch of one band on the CSS `background`, the sounding of the
 *  shallow end of the band and the band word. */
function bandSwatch(band: number, hist: boolean, background: string): string {
  return `<span class="dl-band"><i style="background:${background}">`
    + `<em>${bandTopMetres(band)}</em></i><b>${esc(bandWord(band, hist))}</b></span>`;
}

/** A swatch of one band: the colour of the depth tints over the water top
 *  (R3229), solid, as the map paints it with no lantern. */
function swatch(band: number, hist: boolean, look: DepthLook): string {
  return bandSwatch(band, hist, depthBandCss(band, hist ? 'historical' : 'modern', look));
}

/** The DEPTH section body: the swatches in the depth look the map draws.
 *  The section has no thermocline line (R2831). */
export function depthBodyHtml(hist: boolean, look: DepthLook): string {
  return `<div class="dl-bands">${BANDS.map((b) => swatch(b, hist, look)).join('')}</div>`;
}

/** A swatch of one band in the DEPTH heat look (R2870): the band's heat
 *  ink at its heat alpha over the water. */
function heatSwatch(band: number, hist: boolean): string {
  const cfg = depthHeatCfg(hist ? 'historical' : 'modern');
  const tint = `rgb(${channels(cfg.inks[band - 1]).join(' ')} / ${cfg.alphas[band - 1]})`;
  return bandSwatch(band, hist, `linear-gradient(${tint},${tint}),${WATER_DEEP_TOP}`);
}

/** The body of the HEAT section of the DEPTH heat map (R2870): the four
 *  band swatches of the depth heat look, each with its metres and word. */
export function depthHeatBodyHtml(hist: boolean): string {
  return `<div class="dl-bands">${BANDS.map((b) => heatSwatch(b, hist)).join('')}</div>`;
}

/** Which of the two deep sections stand, and in which picture. */
export interface DeepShown {
  /** The DEPTH section: the depth tints, the depth heat look, or none. */
  depth: 'tints' | 'heat' | null;
  /** The CURRENT section stands. */
  current: boolean;
}

/**
 * The deep sections for a heat mode (R2870). The DEPTH heat map shows the
 * heat swatches and hides the streaks; the CURRENTS heat map shows the
 * current and hides the tints. Each map shows its field while the VIEW
 * pill hides the group. With no local mode, a section stands while the
 * hello carries its field and the reader shows its group.
 */
export function deepShown(
  heat: HeatmapKind, depthField: boolean, currentField: boolean, hidden: readonly string[],
): DeepShown {
  if (heat === HeatmapKind.HEATMAP_KIND_DEPTH) return { depth: depthField ? 'heat' : null, current: false };
  if (heat === HeatmapKind.HEATMAP_KIND_CURRENTS) return { depth: null, current: currentField };
  return {
    depth: depthField && !hidden.includes('depth') ? 'tints' : null,
    current: currentField && !hidden.includes('currents'),
  };
}

/** What the CURRENT section states. */
export interface CurrentLegend {
  /** The strongest current of the field at tide factor 1, px a map hour. */
  strongestPxH: number;
  /** The tide clock now, or null with no table. */
  tide: TideClock | null;
  /** The table of `CatalogRules.tide_steps`. */
  steps: readonly number[];
  /** The world time of day now, in world seconds. */
  clockSec: number;
  /** Map seconds a wall second (`GameState.tacticalScale`): the row states
   *  px a wall minute, the minute of the speed fact of a fleet card. */
  tacticalScale: number;
}

/** A factor as the clock writes it: two places at most. */
function factorText(f: number): string {
  return String(Math.round(f * 100) / 100);
}

/** The streak swatch of card 07 (R2716): a stroke whose tail fades, as a
 *  streak of the current looks runs. */
const STREAK = '<svg class="dl-streak" width="28" height="10" aria-hidden="true">'
  + '<path d="M2 5h8" opacity="0.3"/><path d="M10 5h8" opacity="0.6"/><path d="M18 5h8"/></svg>';

/** The CURRENT section body: the streak row and the tide clock. */
export function currentBodyHtml(hist: boolean, c: CurrentLegend): string {
  const factor = c.tide?.factor ?? 1;
  const n = Math.round((c.strongestPxH * Math.abs(factor) * c.tacticalScale) / 6) / 10;
  let html = `<div class="dl-row">${STREAK}<span>${esc(hist
    ? S.legend_current_row_historical({ n })
    : S.legend_current_row({ n }))}</span></div>`;
  const t = c.tide;
  if (!t) return html;
  const step = t.step + 1, steps = c.steps.length;
  html += `<div class="dl-row dl-tide"><span>${esc(hist
    ? S.legend_tide_historical({ f: factorText(t.factor), step, steps })
    : S.legend_tide({ f: factorText(t.factor), step, steps }))}</span></div>`;
  // The bar of the cycle: one cell a step, its shade by the strength of the
  // step, the steps that turn the flow round in the second ink.
  html += '<div class="dl-cycle" aria-hidden="true">' + c.steps.map((f, i) => {
    const k = Math.round((0.1 + 0.4 * Math.min(1, Math.abs(f))) * 100) / 100;
    return `<i class="${f < 0 ? 'neg' : 'pos'}${i === t.step ? ' now' : ''}" style="--k:${k}"></i>`;
  }).join('') + '</div>';
  const next = hhmm(c.clockSec + t.nextInSec);
  const turn = t.turnInSec < 0 ? '' : hhmm(c.clockSec + t.turnInSec);
  html += `<div class="dl-ends"><span>${esc(hist ? S.legend_tide_next_historical({ at: next }) : S.legend_tide_next({ at: next }))}</span>`
    + `<span>${esc(turn === ''
      ? (hist ? S.legend_tide_steady_historical : S.legend_tide_steady)
      : (hist ? S.legend_tide_turns_historical({ at: turn }) : S.legend_tide_turns({ at: turn })))}</span></div>`;
  return html;
}
